import {
  Container,
  Header,
  KeyValuePairs,
  SpaceBetween,
  Table,
} from "@cloudscape-design/components";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Dropdown,
  Flex,
  Form,
  Row,
  Space,
  Typography,
} from "antd";
import { useParams } from "react-router-dom";
import {
  useAddDocRequestTemplateMutation,
  useDeleteDocumentShareRequestTemplateMutation,
  useGetRequestTemplateByIdQuery,
} from "../../redux/api/requesttemplate/requesttemplate";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  DocumentRequest,
  DocumentRequestResponse,
} from "../../redux/api/documentrequest/types";
import { useContext, useState } from "react";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { FormProvider, useForm } from "react-hook-form";
import IncredableButton from "../../components/button";
import RHFTextField from "../../components/RHF/RHFTextField";
import RHFTextArea from "../../components/RHF/RHFTextArea";
import RHFSelect from "../../components/RHF/RHFSelect";
import { useGetAllCategoryQuery } from "../../redux/api/category/category";
import IncredableContent from "../../components/incredable-content";
import EditRequestTemplateModal from "./edit-request-template-modal";
import DeleteAlertModal from "../../common/Modal/delete-alert-modal";
import { SpinnerContext } from "../../context/spinner/spinner";
import { ToastContext } from "../../context/toast/incredable-toast";
import IncredableDateFormat from "../../common/incredable-date-format";

export default function EditRequestTemplate() {
  const methods = useForm<DocumentRequest>();
  const { id } = useParams();
  const {
    data: requestTemplate,
    isLoading,
    refetch,
  } = useGetRequestTemplateByIdQuery(!!id ? { id: id } : skipToken);
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { data: categories } = useGetAllCategoryQuery();
  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState);
    setSelectedRows([]);
  };
  const [editRequestTemplateModalVisible, setEditRequestTemplateModalVisible] =
    useState<boolean>(false);

  const [addDocumentRequest] = useAddDocRequestTemplateMutation();
  const [deleteDocumentShareRequest] = useDeleteDocumentShareRequestTemplateMutation();
  const [selectedRows, setSelectedRows] = useState<DocumentRequestResponse[]>();

  return (
    <IncredableContent
      spinning={isLoading}
      breadcrumbGroupProps={{
        items: [
          {
            text: "Request Templates",
            href: "/requesttemplates",
          },
          {
            text: "Edit",
            href: "#",
          },
        ],
      }}
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              actions={
                <Space>
                  <Button
                    onClick={() => {
                      !!refetch && refetch();
                    }}
                  >
                    <ReloadOutlined />
                  </Button>
                  <IncredableButton icon={<EditOutlined />} type="primary" onClick={() => setEditRequestTemplateModalVisible(true)}>
                    Edit Template
                  </IncredableButton>
                </Space>
              }
            >
              Template Overview
            </Header>
          }
        >
          <div style={{ paddingTop: "12px" }}>
            <KeyValuePairs
              columns={3}
              items={[
                {
                  label: "Template Name",
                  value: requestTemplate?.documentRequestTemplate?.name,
                },
                {
                  label: "Created Date",
                  value:
                    IncredableDateFormat(requestTemplate?.documentRequestTemplate?.createdDate) ??
                    "-",
                },
                {
                  label: "Description",
                  value:
                    requestTemplate?.documentRequestTemplate?.description ??
                    "-",
                },
              ]}
            />
          </div>
        </Container>

        <Table
          loading={isLoading}
          onSelectionChange={({ detail }) => {
            setSelectedRows([...detail.selectedItems]);
          }}
          onRowClick={(onRowClickDetails) => {
            const selectedItem = onRowClickDetails?.detail?.item;
            if (selectedItem) {
              if (selectedRows?.at(0)?.id === selectedItem?.id) {
                setSelectedRows([]);
              } else {
                setSelectedRows([selectedItem]);
              }
            }
          }}
          selectedItems={selectedRows || []}
          columnDefinitions={[
            {
              id: "title",
              header: "Title",
              cell: (item) => item.title,
              sortingField: "title",
              isRowHeader: true,
            },
            {
              id: "description",
              header: "Description",
              cell: (item) => item?.description,
              sortingField: "description",
            },

            {
              id: "category",
              header: "Category",
              cell: (item) => item?.category?.name || "-",
            },
          ]}
          items={requestTemplate?.documentShareRequests ?? []}
          loadingText="Loading resources"
          selectionType="single"
          trackBy="id"
          header={
            <div style={{ marginTop: "8px", marginBottom: "12px" }}>
              <Header
                variant="h3"
                actions={
                  <Space size="middle">
                    <Button
                      onClick={() => {
                        !!refetch && refetch();
                      }}
                    >
                      <ReloadOutlined />
                    </Button>
                    <Dropdown
                      menu={{
                        disabled: selectedRows?.length !== 1,
                        items: [
                          {
                            label: "Edit",
                            key: "edit",
                            icon: <EditOutlined />,
                            onClick: () => {
                              if (!!selectedRows?.at(0)) {
                                methods.setValue(
                                  "id",
                                  selectedRows?.at(0)?.id ?? ""
                                );
                                methods.setValue(
                                  "title",
                                  selectedRows?.at(0)?.title ?? ""
                                );
                                !!selectedRows?.at(0)?.category?.id &&
                                  methods.setValue(
                                    "categoryId",
                                    selectedRows?.at(0)?.category?.id ?? ""
                                  );
                                methods.setValue(
                                  "description",
                                  selectedRows?.at(0)?.description ?? ""
                                );
                                toggleDrawer();
                              }
                            },
                          },
                          {
                            label: "Delete",
                            key: "delete",
                            onClick: () => setIsDeleteModalOpen(true),
                            icon: <DeleteOutlined />,
                            danger: true,
                          },
                        ],
                      }}
                    >
                      <Button>
                        Actions
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: "#2B6BE6",
                        },
                      }}
                    >
                      <Button icon={<PlusOutlined/>}onClick={toggleDrawer} type="primary">
                        Add Document Request
                      </Button>
                    </ConfigProvider>
                  </Space>
                }
              >
                Associated Document Requests
              </Header>
            </div>
          }
        />
      </SpaceBetween>
      <Drawer
        open={isOpen}
        onClose={toggleDrawer}
        direction="right"
        size="70vw"
      >
        <div style={{ padding: "24px" }}>
          <Card
            title={
              <Header>
                {!!methods.getValues("id")
                  ? "Edit Document Request"
                  : "Add Document Request"}
              </Header>
            }
          >
            <FormProvider {...methods}>
              <Form
                name="editsection"
                layout="vertical"
                onFinish={methods.handleSubmit(
                  async (data: DocumentRequest) => {
                    if (!!id)
                      addDocumentRequest({ id: id, documentRequest: data });
                    toggleDrawer();
                    methods.reset();
                  }
                )}
                autoComplete="off"
              >
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col span={8}>
                    <RHFTextField
                      formItemProps={{
                        label: "Title",
                      }}
                      inputProps={{}}
                      controllerProps={{
                        name: `title`,
                      }}
                      rules={{ required: "This field is required" }}
                    />
                  </Col>
                  <Col span={8}>
                    <RHFSelect
                      formItemProps={{ label: "Category" }}
                      controllerProps={{
                        name: `categoryId`,
                      }}
                      selectProps={{
                        allowClear: true,
                        showSearch: true,
                        style: { width: "100%" },
                        placeholder: "Search to Select",
                        options: categories?.map((facility) => {
                          return {
                            label: facility?.name,
                            value: facility?.id,
                          };
                        }),
                      }}
                      rules={{ required: "This field is required" }}
                    />
                  </Col>
                  <Col span={16}>
                    <RHFTextArea
                      formItemProps={{
                        label: "Description",
                      }}
                      textAreaProps={{}}
                      controllerProps={{
                        name: `description`,
                      }}
                    />
                  </Col>
                  <Col span={16}>
                    <Flex justify="flex-end">
                      <Space>
                        <IncredableButton type="primary" htmlType="submit">
                          Save
                        </IncredableButton>
                        <IncredableButton
                          secondary
                          onClick={() => {
                            methods.reset();
                            !!toggleDrawer && toggleDrawer();
                          }}
                        >
                          Cancel
                        </IncredableButton>
                      </Space>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </FormProvider>
          </Card>
        </div>
      </Drawer>
      <EditRequestTemplateModal
        closeModal={() => {
          setEditRequestTemplateModalVisible(false);
        }}
        visible={editRequestTemplateModalVisible}
      />
      <DeleteAlertModal
        visible={isDeleteModalOpen}
        action={async () => {
          if (selectedRows?.at(0)?.id) {
            setIsDeleteModalOpen(false);
            spinnerContext?.showSpinner();
            try {
              await deleteDocumentShareRequest({
                id: String(id),
                documentShareRequestId: String(selectedRows?.at(0)?.id)
              }).unwrap();
              toastContext?.openSuccessNotification({
                message: `Document Share Request template deleted successfully`,
                placement: "topRight",
              });
            } catch (error: any) {
              toastContext?.openErrorNotification({
                message: error?.data?.errorMessage || `Failed to delete document share request template`,
                placement: "topRight",
              });
            }
            spinnerContext?.hidespinner();
            setSelectedRows([]);
          }
        }}
        closeModal={() => setIsDeleteModalOpen(false)}
        header="Delete Request Template"
        content={
          <Typography.Text>
            Name:{" "}
            <Typography.Text strong>
              {selectedRows?.at(0)?.title}
            </Typography.Text>
          </Typography.Text>
        }
        description="Are you sure you want to delete this document share request template?"
      />
    </IncredableContent>
  );
}
